import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    ChevronLeft,
    CreditCard,
    Truck,
    Mail,
    User,
    MapPin,
    AlertCircle,
    Loader2,
    CheckCircle,
    ShoppingBag,
    ArrowRight,
    X
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { useNotification } from "../context/NotificationContext";
import { ShopService } from "../services/user/ShopServices";
import { useAuth } from "../services/AuthService";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";

const validateEmail = (email, silent = false, setEmailError = () => { }) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
        if (!silent) setEmailError("Email is required");
        return false;
    }
    if (!re.test(email)) {
        if (!silent) setEmailError("Invalid email format");
        return false;
    }
    setEmailError("");
    return true;
};

export default function Checkout() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isCanceled = searchParams.get("canceled") === "true";
    const isSuccess = searchParams.get("success") === "true";
    const successReference = searchParams.get("reference");
    const { cart, hydrated, clearCart } = useCart();
    const { showNotification } = useNotification();
    const { user, isAuthenticated } = useAuth();

    const [showSuccessModal, setShowSuccessModal] = useState(false);
    // Form State
    const [formData, setFormData] = useState({
        email: "",
        full_name: "",
        address: ""
    });
    const [emailError, setEmailError] = useState("");

    // Auth State
    const [emailStatus, setEmailStatus] = useState(null); // 'checking', 'exists', 'new', null
    const [isCustomer, setIsCustomer] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [otpProcessing, setOtpProcessing] = useState(false);
    const [otpInput, setOtpInput] = useState("");
    const debounceTimer = useRef(null);

    // Status State
    const [loading, setLoading] = useState(true); // Initial page load
    const [isvalidating, setIsValidating] = useState(false); // Background validation
    const [submitting, setSubmitting] = useState(false);
    const [reference, setReference] = useState(null);
    const [validatedCart, setValidatedCart] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [serverErrors, setServerErrors] = useState([]);
    const isFirstLoad = useRef(true);

    const hasPhysicalItems = validatedCart.some(item => item.category_type !== "Music Content");

    const handleInitializeCheckout = useCallback(async (isInitial = false) => {
        if (!cart.length) {
            setLoading(false);
            return;
        }
        try {
            if (isInitial) setLoading(true);
            else setIsValidating(true);

            const response = await ShopService.initCheckout(cart);
            if (response.status === "success") {
                setReference(response.reference);
                setValidatedCart(response.validation.items);
                setTotalAmount(response.validation.total_amount);
                setServerErrors(response.validation.errors);

                if (response.validation.errors.length > 0) {
                    showNotification({
                        type: "warning",
                        title: "Cart Updated",
                        message: "Some items in your cart were updated due to availability."
                    });
                }
            }
        } catch (error) {
            console.error("Checkout init error:", error);
            if (isInitial) {
                showNotification({
                    type: "failed",
                    title: "Initialization Failed",
                    message: "Could not initialize checkout. Please try again."
                });
            }
        } finally {
            setLoading(false);
            setIsValidating(false);
        }
    }, [cart, showNotification]);

    // --- Target optimization: Redirect to shop if cart is empty ---
    useEffect(() => {
        if (hydrated && cart.length === 0 && !isSuccess && !loading) {
            showNotification({
                type: "info",
                title: "Cart Empty",
                message: "Your cart is empty. Please add some products to checkout."
            });
            navigate("/shop");
        }
    }, [hydrated, cart.length, isSuccess, loading, navigate, showNotification]);

    useEffect(() => {
        if (!hydrated) return;

        // Payment was successful — show modal and clear cart
        if (isSuccess && successReference) {
            const modalShown = sessionStorage.getItem(`order_success_${successReference}`);
            if (!modalShown) {
                clearCart();
                setValidatedCart([]);
                setShowSuccessModal(true);
                sessionStorage.setItem(`order_success_${successReference}`, 'true');
            }
            setLoading(false);
            return;
        }

        if (isCanceled) {
            const cancelRef = searchParams.get("reference");
            if (cancelRef) {
                ShopService.cancelCheckout(cancelRef)
                    .then(() => {
                        console.log(`Successfully cleaned up cancelled order: ${cancelRef}`);
                    })
                    .catch(err => {
                        console.error("Failed to clean up cancelled order:", err);
                    });
            }
            showNotification({
                type: "warning",
                title: "Payment Cancelled",
                message: "You cancelled the payment. Your cart is still saved."
            });
            // Clear URL params to avoid re-triggering notification or cleanup
            navigate("/checkout", { replace: true });
        }

        if (isFirstLoad.current) {
            handleInitializeCheckout(true);
            isFirstLoad.current = false;
        } else {
            handleInitializeCheckout(false);
        }

        // If user is logged in, pre-fill and lock contact details
        if (isAuthenticated && user) {
            setFormData(prev => ({
                ...prev,
                email: user.email,
                full_name: user.full_name
            }));
            setEmailStatus('exists');
            setIsCustomer(true);
            setOtpVerified(true);
        }
    }, [hydrated, cart.length, navigate, handleInitializeCheckout, showNotification, isCanceled, isSuccess, clearCart, searchParams, successReference, isAuthenticated, user]);

    const checkEmailDebounced = useCallback((email) => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        if (!validateEmail(email, true)) {
            setEmailStatus(null);
            setOtpSent(false);
            setOtpVerified(false);
            return;
        }

        setEmailStatus('checking');
        debounceTimer.current = setTimeout(async () => {
            try {
                const res = await ShopService.checkEmail(email);
                setEmailStatus(res.exists ? 'exists' : 'new');
                setIsCustomer(!!res.is_customer);

                // If existing customer, pre-fill and disable name
                if (res.exists && res.full_name) {
                    setFormData(prev => ({ ...prev, full_name: res.full_name }));
                }

                // If backend says it's already verified (pending submission), skip OTP step
                if (res.verified) {
                    setOtpVerified(true);
                    setOtpSent(false);
                } else if (res.exists) {
                    setOtpSent(false);
                    setOtpVerified(false);
                }
            } catch (err) {
                setEmailStatus(null);
                setIsCustomer(false);
            }
        }, 500);
    }, []);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === "email" && !isAuthenticated) {
            setOtpVerified(false);
            setOtpSent(false);
            checkEmailDebounced(value);
        }
    }, [checkEmailDebounced, isAuthenticated]);

    const handleSendOtp = async () => {
        if (!validateEmail(formData.email, false, setEmailError)) return;
        try {
            setOtpProcessing(true);
            const res = await ShopService.sendOtp(formData.email);
            if (res.status === "success") {
                setOtpSent(true);
                showNotification({ type: "success", title: "OTP Sent", message: res.message });
            }
        } catch (error) {
            showNotification({ type: "failed", title: "OTP Failed", message: error.response?.data?.message || "Failed to send OTP" });
        } finally {
            setOtpProcessing(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otpInput) return;
        try {
            setOtpProcessing(true);
            const res = await ShopService.verifyOtp(formData.email, otpInput);
            if (res.status === "success") {
                setOtpVerified(true);
                showNotification({ type: "success", title: "Verified", message: "Email verified successfully!" });
            }
        } catch (error) {
            showNotification({ type: "failed", title: "Verification Failed", message: error.response?.data?.message || "Invalid OTP" });
        } finally {
            setOtpProcessing(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateEmail(formData.email, false, setEmailError)) return;
        if (emailStatus === 'checking') {
            showNotification({ type: "warning", message: "Please wait while we verify your email." });
            return;
        }
        if (emailStatus === 'new' && !otpVerified) {
            showNotification({ type: "warning", title: "Verification Required", message: "Please verify your email before proceeding." });
            return;
        }

        if (!formData.full_name) {
            showNotification({ type: "failed", message: "Full name is required" });
            return;
        }
        if (hasPhysicalItems && !formData.address) {
            showNotification({ type: "failed", message: "Shipping address is required for merchandise" });
            return;
        }

        try {
            setSubmitting(true);
            const submitData = {
                ...formData,
                reference,
                cart: validatedCart,
                is_new_user: !isCustomer
            };
            const response = await ShopService.submitCheckout(submitData);

            if (response.status === "success" && response.data?.payment_url) {
                showNotification({
                    type: "success",
                    title: "Order Processed",
                    message: "Redirecting to secure payment gateway..."
                });

                // Redirect user to Stripe Checkout
                setTimeout(() => {
                    window.location.href = response.data.payment_url;
                }, 1000);
            } else {
                showNotification({
                    type: "failed",
                    title: "Payment Initialization Failed",
                    message: "Unable to retrieve payment session."
                });
            }
        } catch (error) {
            const msg = error.response?.data?.message || "Checkout failed. Please try again.";
            showNotification({
                type: "failed",
                title: "Checkout Error",
                message: msg
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading && !isSuccess) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white pt-20">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 animate-spin mx-auto text-jazz-brown mb-4" />
                    <p className="text-xs font-bold tracking-[2px] uppercase text-black">Securing Session...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#fafafa]">
            {/* ── Payment Success Modal ── */}
            <AnimatePresence>
                {showSuccessModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
                    >
                        <motion.div
                            initial={{ scale: 0.85, opacity: 0, y: 30 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.85, opacity: 0, y: 30 }}
                            transition={{ type: "spring", damping: 22, stiffness: 260 }}
                            className="relative w-full max-w-md bg-white rounded-3xl p-6 sm:p-8 shadow-2xl max-h-[90vh] overflow-y-auto"
                        >
                            <button
                                onClick={() => setShowSuccessModal(false)}
                                className="absolute right-6 top-6 text-black/20 hover:text-black transition-colors"
                            >
                                <X size={20} />
                            </button>

                            {/* Green check icon */}
                            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle className="text-green-500" size={34} />
                            </div>

                            <h2 className="text-2xl font-serif italic text-center mb-2">Order Confirmed!</h2>
                            {successReference && (
                                <p className="text-[10px] text-center text-black font-bold uppercase tracking-[2px] mb-6">
                                    Ref: <span className="text-jazz-brown">{successReference}</span>
                                </p>
                            )}

                            {/* Email notice */}
                            <div className="flex items-start gap-3 bg-black/[0.03] rounded-2xl p-4 mb-6">
                                <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center shrink-0 shadow-sm">
                                    <Mail className="text-jazz-brown" size={16} />
                                </div>
                                <p className="text-xs text-black font-medium leading-relaxed">
                                    We've sent your order confirmation and digital content access details to your inbox.
                                </p>
                            </div>

                            {/* Next Steps */}
                            <p className="text-[10px] text-black/60 uppercase font-bold tracking-[2px] mb-4 text-center">Next Steps</p>
                            <div className="space-y-3">
                                <button
                                    onClick={() => {
                                        setShowSuccessModal(false);
                                        navigate("/dashboard");
                                    }}
                                    className="w-full flex items-center justify-between p-4 bg-black text-white rounded-xl hover:bg-jazz-brown transition-all group"
                                >
                                    <span className="text-xs font-bold uppercase tracking-widest">Go to Dashboard</span>
                                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button
                                    onClick={() => {
                                        setShowSuccessModal(false);
                                        navigate("/shop");
                                    }}
                                    className="w-full flex items-center justify-between p-4 border border-black/10 rounded-xl hover:border-black transition-all group bg-transparent"
                                >
                                    <span className="text-xs font-bold uppercase tracking-widest text-black">Continue Shopping</span>
                                    <ShoppingBag size={16} className="text-black/40 group-hover:text-black" />
                                </button>
                            </div>

                            {/* New-user note */}
                            <p className="mt-6 text-[10px] text-center text-black/60 leading-relaxed italic">
                                If you're a new user, check your email for your temporary login credentials.
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <JazzHeader />
            <SEO title="Checkout" url="/checkout" />
            <main className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-12">
                <div className="max-w-6xl mx-auto">
                    <Link to="/shop" className="inline-flex items-center gap-2 text-xs font-bold tracking-[1px] uppercase text-black hover:text-jazz-brown transition-colors mb-12">
                        <ChevronLeft size={14} />
                        Back to Shop
                    </Link>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                        {/* Left: Contact & Shipping */}
                        <div className="space-y-12">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <h1 className="text-4xl font-serif italic mb-2">Checkout</h1>
                                    <p className="text-xs text-black font-medium">Complete your details to finalize your order.</p>
                                </div>
                                {isvalidating && (
                                    <div className="flex items-center gap-2 bg-black/5 px-3 py-1.5 rounded-full animate-pulse">
                                        <Loader2 size={12} className="animate-spin text-jazz-brown" />
                                        <span className="text-[9px] font-bold uppercase tracking-wider text-black/60">Syncing</span>
                                    </div>
                                )}
                            </div>

                            {serverErrors.length > 0 && (
                                <div className="bg-red-50 border border-red-100 p-4 rounded-xl flex gap-3 text-red-600">
                                    <AlertCircle size={18} className="shrink-0" />
                                    <div className="text-xs space-y-1">
                                        <p className="font-bold uppercase tracking-wider">Stock Notice</p>
                                        <ul className="list-disc list-inside">
                                            {serverErrors.map((err, i) => <li key={i}>{err}</li>)}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            <form id="checkout-form" onSubmit={handleSubmit} className="space-y-8">
                                <div className="space-y-6">
                                    <h3 className="text-[10px] font-bold tracking-[3px] uppercase text-black border-b border-black/10 pb-2">1. Contact Information</h3>
                                    <div className="space-y-4">
                                        <div className="relative">
                                            <Mail className="absolute left-4 top-[26px] -translate-y-1/2 text-black/60" size={18} />
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="Email Address"
                                                required
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                readOnly={isAuthenticated}
                                                className={`w-full bg-white border ${emailError ? 'border-red-300' : 'border-black/10'} rounded-xl py-4 pl-12 pr-12 text-sm font-semibold text-black focus:outline-none focus:border-jazz-brown transition-colors shadow-sm ${isAuthenticated ? 'bg-black/5 opacity-70 cursor-not-allowed' : ''}`}
                                            />
                                            {emailStatus === 'checking' && (
                                                <Loader2 className="absolute right-4 top-[26px] -translate-y-1/2 text-jazz-brown animate-spin" size={18} />
                                            )}
                                            {emailStatus === 'exists' && (
                                                <CheckCircle className="absolute right-4 top-[26px] -translate-y-1/2 text-green-500" size={18} />
                                            )}

                                            {emailError && <p className="mt-2 text-[10px] text-red-500 font-bold uppercase ml-4">{emailError}</p>}

                                            {emailStatus === 'new' && !otpVerified && (
                                                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-3">
                                                    <p className="text-[10px] text-jazz-brown font-semibold ml-4 mb-2">New email detected. Verification required.</p>
                                                    {!otpSent ? (
                                                        <button type="button" onClick={handleSendOtp} disabled={otpProcessing} className="bg-black text-white text-[10px] font-bold tracking-[1px] uppercase py-2 px-4 rounded-lg ml-4 hover:bg-jazz-brown transition-colors flex items-center gap-2">
                                                            {otpProcessing ? <Loader2 size={12} className="animate-spin" /> : null}
                                                            Send Verification Code
                                                        </button>
                                                    ) : (
                                                        <div className="flex items-center gap-2 ml-4">
                                                            <input
                                                                type="text"
                                                                placeholder="Enter 6-digit OTP"
                                                                value={otpInput}
                                                                onChange={(e) => setOtpInput(e.target.value)}
                                                                className="border border-black/10 rounded-lg py-2 px-3 text-sm font-semibold focus:outline-none focus:border-jazz-brown w-40"
                                                            />
                                                            <button type="button" onClick={handleVerifyOtp} disabled={otpProcessing || !otpInput} className="bg-black text-white text-[10px] font-bold tracking-[1px] uppercase py-2 px-4 rounded-lg hover:bg-jazz-brown transition-colors disabled:opacity-50 flex items-center gap-2">
                                                                {otpProcessing ? <Loader2 size={12} className="animate-spin" /> : null}
                                                                Verify
                                                            </button>
                                                        </div>
                                                    )}
                                                </motion.div>
                                            )}
                                            {otpVerified && emailStatus === 'new' && (
                                                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="mt-2 flex items-center gap-1.5 ml-4">
                                                    <CheckCircle className="text-green-500" size={14} />
                                                    <span className="text-[10px] text-green-600 font-bold uppercase tracking-[1px]">Email Verified</span>
                                                </motion.div>
                                            )}
                                        </div>
                                        <div className="relative">
                                            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-black/60" size={18} />
                                            <input
                                                type="text"
                                                name="full_name"
                                                placeholder="Full Name"
                                                required
                                                value={formData.full_name}
                                                onChange={handleInputChange}
                                                readOnly={isCustomer}
                                                className={`w-full bg-white border border-black/10 rounded-xl py-4 pl-12 pr-4 text-sm font-semibold text-black focus:outline-none focus:border-jazz-brown transition-colors shadow-sm ${isCustomer ? 'bg-black/5 opacity-70 cursor-not-allowed' : ''}`}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {hasPhysicalItems && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="space-y-6"
                                    >
                                        <h3 className="text-[10px] font-bold tracking-[3px] uppercase text-black/60 border-b border-black/10 pb-2">2. Shipping Details</h3>
                                        <div className="relative">
                                            <MapPin className="absolute left-4 top-4 text-black/60" size={18} />
                                            <textarea
                                                name="address"
                                                placeholder="Full Delivery Address"
                                                required
                                                value={formData.address}
                                                onChange={handleInputChange}
                                                rows={3}
                                                className="w-full bg-white border border-black/10 rounded-xl py-4 pl-12 pr-4 text-sm font-semibold text-black focus:outline-none focus:border-jazz-brown transition-colors shadow-sm"
                                            />
                                        </div>
                                        <div className="bg-jazz-brown/5 p-4 rounded-xl flex items-center gap-3">
                                            <Truck className="text-jazz-brown" size={18} />
                                            <p className="text-[10px] text-jazz-brown font-medium leading-relaxed">
                                                Ships globally within 3-5 business days. Tracking info will be sent to your email.
                                            </p>
                                        </div>
                                    </motion.div>
                                )}
                            </form>
                        </div>

                        {/* Right: Order Summary */}
                        <div className="lg:sticky lg:top-32 h-fit">
                            <div className="bg-white border border-black/5 rounded-2xl p-8 shadow-sm">
                                <div className="mb-8">
                                    <h3 className="text-xl font-serif italic mb-1">Order Summary</h3>
                                    <p className="text-[10px] text-black font-bold uppercase tracking-[2px]">Ref: <span className="text-jazz-brown">{reference}</span></p>
                                </div>

                                <div className="space-y-6 mb-8 overflow-y-auto max-h-[400px] pr-2">
                                    {validatedCart.map((item) => (
                                        <div key={item.id} className="flex gap-4">
                                            <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0 border border-black/5">
                                                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-sm font-bold text-black truncate">{item.name}</h4>
                                                <div className="flex justify-between items-center mt-1">
                                                    <p className="text-xs text-black font-medium">
                                                        {item.category_type === "Music Content" ? "" : `${item.quantity} × `}${item.price.toFixed(2)}
                                                    </p>
                                                    <p className="text-sm font-bold font-serif">${item.subtotal.toFixed(2)}</p>
                                                </div>
                                                {item.category_type === "Music Content" && (
                                                    <span className="text-[9px] font-bold text-jazz-brown uppercase tracking-wider bg-jazz-brown/5 px-2 py-0.5 rounded-full mt-2 inline-block">Digital</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="space-y-4 pt-6 border-t border-black/5">
                                    <div className="flex justify-between text-xs font-bold tracking-[1px] uppercase text-black">
                                        <span>Subtotal</span>
                                        <span className="text-black">${totalAmount.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-xs font-bold tracking-[1px] uppercase text-black">
                                        <span>Shipping</span>
                                        <span className="text-black text-[9px] text-right">{hasPhysicalItems ? 'To be communicated by representative' : 'Free (Digital Items)'}</span>
                                    </div>
                                    <div className="flex justify-between items-end pt-4">
                                        <span className="text-[10px] font-bold tracking-[2px] uppercase text-black">Total to pay</span>
                                        <span className="text-3xl font-bold font-serif text-black">${totalAmount.toFixed(2)}</span>
                                    </div>
                                </div>

                                <div className="mt-8 space-y-4">
                                    <button
                                        form="checkout-form"
                                        type="submit"
                                        disabled={submitting || !!emailError}
                                        className="w-full bg-black text-white py-5 rounded-xl text-xs font-bold tracking-[3px] uppercase hover:bg-jazz-brown disabled:bg-black/20 disabled:cursor-not-allowed transition-all duration-500 shadow-xl flex items-center justify-center gap-3"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <CreditCard size={18} />
                                                Pay Now — ${totalAmount.toFixed(2)}
                                            </>
                                        )}
                                    </button>
                                    <div className="pt-6 border-t border-black/10">
                                        <div className="flex items-center justify-center gap-2 mb-3">
                                            <div className="w-5 h-5 bg-green-50 rounded-full flex items-center justify-center">
                                                <CheckCircle className="text-green-500" size={12} />
                                            </div>
                                            <span className="text-[10px] font-bold tracking-[1.5px] uppercase text-black">Secure Payment with Stripe</span>
                                        </div>
                                        <p className="text-[9px] text-black font-medium text-center uppercase tracking-widest px-4 leading-relaxed opacity-80">
                                            By clicking pay now, you agree to our terms of service and shipping policies.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <JazzFooter />
        </div>
    );
}
